import React, { FC, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { RouteComponentProps } from 'react-router-dom';
import axios from '../../../utils/axios';
import { API_URL, PAGE_SIZE } from '../../../constants';
import styles from '../../../styledComponents';
import { TextField, Button, LoaderOverlay } from '../../../components/library';
import { StripedDataGrid } from '../../../styledComponents/datagrid';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { renderCellExpand } from '../../../components/library/DataGrid/renderCellExpand';
import {
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { UpcSearchModal } from '../../service/UpcSearchModal';
// import { useInventory } from '../../catalog/utils/useInventory';
import { Inventory, InventoryHistoryItem } from '../../../services/inventory/types';
import HoverableImageThumbnail from '../../catalog/productImages/HoverableImageThumbnail';
import { getZoneNameFromZone } from '../../catalog/utils/inventory/utils';
import { useKeyPress } from '../../../utils/hooks/useKeyPress';
import { FormatDateString, FormatDateTimeString } from '../../../utils/helper';
import ItemCatalog from '../../../components/nav/svgs/ItemCatalog';
import ThreeSixty from '../../../components/nav/svgs/ThreeSixty';
import { groupInventoryByZone } from '../../../services/fulfillment/utils';
import { InspectionStatusBadge } from '../../service/serviceTicket/InspectionStatusBadge';

export const INVENTORY_LOCATION_DETAILS_ROUTE = '/inventory/item-details';

const { PageWrapper } = styles;

const InlineTextField = styled(TextField)`
  display: inline-block;
`;

const historyColumns: GridColDef[] = [
  {
    headerName: 'Date',
    field: 'date',
    renderCell: (params: GridRenderCellParams) => {
      const hoverValue = FormatDateTimeString(params.value);
      params.value = FormatDateString(params.value);
      return renderCellExpand(params, hoverValue);
    },
    flex: 1,
  },
  {
    headerName: 'Type',
    field: 'type',
    flex: 1,
    valueGetter: ({ row }) => {
      return { type: row.type, transferType: row.transferType };
    },
    renderCell: ({ value }) => {
      const typeLabels: { [key: string]: string } = {
        transfer: `Transfer (${value.transferType})`,
        adjustment: 'Adjustment',
      };
      return typeLabels[value.type] || value.type;
    },
  },
  {
    headerName: 'Qty',
    field: 'quantity',
    flex: 0.5,
    valueGetter: ({ row }) => {
      if (row.type === 'transfer') {
        return row.transferItem?.quantityTransferred || 0;
      }
      if (row.type === 'adjustment') {
        return row.adjustmentItem?.quantityAdjusted || 0;
      }
      return '';
    },
  },
  {
    headerName: 'Details',
    field: 'details',
    flex: 4,
    renderCell: renderCellExpand,
    valueGetter: ({ row }) => {
      if (row.type === 'transfer') {
        const fromBin = row.transferItem?.fromWarehouseBin?.code || 'Unknown';
        const fromZoneName = getZoneNameFromZone(row.transferItem?.fromWarehouseBin?.warehouseZone);
        const toBin = row.transferItem?.toWarehouseBin?.code || 'Unknown';
        const toZoneName = getZoneNameFromZone(row.transferItem?.toWarehouseBin?.warehouseZone);
        const fromStatus = row.transferItem?.fromInspectionStatus?.name;
        const toStatus = row.transferItem?.toInspectionStatus?.name;

        const fromLocation = fromZoneName ? `${fromZoneName} > ${fromBin}` : fromBin;
        const toLocation = toZoneName ? `${toZoneName} > ${toBin}` : toBin;
        const fromStatusText = fromStatus ? ` (${fromStatus})` : '';
        const toStatusText = toStatus ? ` (${toStatus})` : '';
        return `From: ${fromLocation}${fromStatusText} → To: ${toLocation}${toStatusText}`;
      }
      if (row.type === 'adjustment') {
        const bin = row.adjustmentItem?.warehouseBin?.code || 'Unknown';
        const zoneName = getZoneNameFromZone(row.adjustmentItem?.warehouseBin?.warehouseZone);
        const reason = row.adjustmentReason?.description || 'Unknown';
        const location = zoneName ? `${zoneName} > ${bin}` : bin;
        return `Bin: ${location} | Reason: ${reason}`;
      }
      return '';
    },
  },
  {
    headerName: 'User',
    field: 'user',
    valueGetter: (params: GridValueGetterParams) => {
      const user = params.row.createdByUser;
      if (user && user.name) {
        const nameParts = user.name.split(' ');
        if (nameParts.length >= 2) {
          const firstName = nameParts[0];
          const lastName = nameParts[nameParts.length - 1];
          return `${firstName} ${lastName.charAt(0)}.`;
        }
        return user.name;
      }
      return user?.email || `User ${params.row.createdByUserId}`;
    },
    renderCell: (params: GridRenderCellParams) => {
      const user = params.row.createdByUser;
      if (!user) {
        params.value = `User ${params.row.createdByUserId}`;
        return renderCellExpand(params);
      }

      const displayName = user.name ? user.name : user.email;
      const hoverValue = `${displayName} (${user.email})`;
      params.value = user.name
        ? (() => {
            const nameParts = user.name.split(' ');
            if (nameParts.length >= 2) {
              const firstName = nameParts[0];
              const lastName = nameParts[nameParts.length - 1];
              return `${firstName} ${lastName.charAt(0)}.`;
            }
            return user.name;
          })()
        : user.email;

      return renderCellExpand(params, hoverValue);
    },
    flex: 1,
  },
];

interface SkuRelocatorProps {
  sku?: string;
}

const InventoryLocationDetails: FC<Partial<RouteComponentProps> & SkuRelocatorProps> = ({
  location: windowLocation,
  sku: propSku,
}) => {
  const skuParam = windowLocation ? new URLSearchParams(windowLocation.search).get('sku') : null;
  // Can pass in initial sku as a query param or prop, prefer query param
  const initialSku = skuParam || propSku || '';

  const [loading, setLoading] = useState<boolean>(false);
  const [upcModalOpen, setUpcModalOpen] = useState(false);

  const [sku, setSku] = useState<string>(initialSku);
  const [isSnowflakeSku, setIsSnowflakeSku] = useState<boolean>(false);
  const [currentInventoryDetails, setCurrentInventoryDetails] = useState<Inventory[] | undefined>(
    undefined,
  );
  const [historyItems, setHistoryItems] = useState<InventoryHistoryItem[]>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  // const { warehouseZones } = useInventory({ typeaheadVal: undefined, zoneId: undefined });

  const [startSearch, setStartSearch] = useState<boolean>(false);
  useKeyPress({ activationKey: 'enter' }, () => setStartSearch(true));
  useEffect(() => {
    const getCurrentSkuInventoryDetails = async () => {
      setLoading(true);
      setCurrentInventoryDetails(undefined);
      setHistoryItems([]);
      if (sku) {
        try {
          const [inventoryRes, historyRes] = await Promise.all([
            axios.get(`${API_URL}/inventory/skus/${sku}/inventoryRecords`),
            axios.get(`${API_URL}/inventory/skus/${sku}/history`),
          ]);

          const currentInvDetails = inventoryRes?.data;
          setIsSnowflakeSku(!!currentInvDetails[0]?.isRefurbished);
          setCurrentInventoryDetails(currentInvDetails);
          setHistoryItems(historyRes?.data || []);
        } catch (err) {
          console.error(
            err?.response?.data?.message
              ? err.response.data.message
              : err?.message
              ? err?.message
              : 'An unknown error occurred',
          );
        }
      }
      setLoading(false);
      setStartSearch(false);
    };
    if (startSearch && !loading) {
      getCurrentSkuInventoryDetails();
    }
  }, [loading, sku, startSearch]);

  const handleSkuByUpc = (value: string) => {
    setUpcModalOpen(false);
    setSku(value);
    setStartSearch(true);
  };

  const handleRowClick = (params: any) => {
    const { row } = params;

    if (row.type === 'transfer') {
      const transferUrl = `/inventory/transfers/${row.id}`;
      window.open(transferUrl, '_blank');
    } else if (row.type === 'adjustment') {
      const adjustmentUrl = `/inventory/adjustments/${row.id}`;
      window.open(adjustmentUrl, '_blank');
    }
    // inventory_history rows do nothing on click
  };

  const skuInputRef = React.useRef<HTMLInputElement>(null);

  const theme = useTheme();
  return (
    <PageWrapper>
      <Box
        style={{
          width: '100%',
          maxWidth: '700px',
          marginTop: theme.spacing(8),
        }}
      >
        <LoaderOverlay loading={loading} />
        <Typography variant="h4" gutterBottom>
          Inventory Item Lookup
        </Typography>
        <Box
          style={{
            border: `${theme.palette.grey[500]} 0.1em solid`,
            backgroundColor: theme.palette.grey[100],
            padding: theme.spacing(3),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            style={{
              padding: '1em 0',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <InlineTextField
                style={{ width: '100%' }}
                tabIndex={0}
                autoFocus
                id="SkuRelocateSKU"
                name="sku"
                value={sku}
                onChange={e => setSku(e.target.value)}
                placeholder="Enter SKU"
                label="SKU"
                variant="outlined"
                inputRef={skuInputRef}
              />
              <span style={{ padding: '0 1rem' }}>or</span>
              <Button onClick={() => setUpcModalOpen(true)} ordinality="secondary" tabIndex={-1}>
                Search by UPC
              </Button>
              {upcModalOpen && (
                <UpcSearchModal
                  open={upcModalOpen}
                  handleClose={() => setUpcModalOpen(false)}
                  setSku={handleSkuByUpc}
                />
              )}
            </Box>
            <Box style={{ marginTop: '1em' }}>
              <Button
                onClick={() => setStartSearch(true)}
                disabled={!sku}
                ordinality="primary"
                tabIndex={-1}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Box>

        {currentInventoryDetails &&
          currentInventoryDetails.length > 0 &&
          (() => {
            const first = currentInventoryDetails[0];
            const imageUrl = first.productVariant?.product?.images?.[0]?.imageUrl ?? undefined;
            const title =
              first.productVariant?.product?.displayTitle ||
              first.productVariant?.product?.title ||
              null;
            const upc = first.productVariant?.upc ?? null;
            const mpn = first.productVariant?.mpn ?? null;
            const productId = first.productVariant?.productId ?? null;
            return (
              <Box
                style={{
                  border: `${theme.palette.grey[500]} 0.1em solid`,
                  marginTop: theme.spacing(2),
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '56px 1fr auto auto',
                    gap: 4,
                    alignItems: 'start',
                    px: 2,
                    py: 1.5,
                  }}
                >
                  {/* Thumbnail */}
                  <HoverableImageThumbnail src={imageUrl} identifier={first.sku} />

                  {/* Item details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.5px' }}>
                        {first.sku}
                      </Typography>
                      {productId && (
                        <Tooltip title="Open in Catalog">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`/catalog/products/${productId}`, '_blank')}
                            sx={{ color: 'grey.400', '&:hover': { color: 'primary.main' } }}
                          >
                            <ItemCatalog />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Open Product 360">
                        <IconButton
                          size="small"
                          onClick={() => window.open(`/products/360view/${first.sku}`, '_blank')}
                          sx={{ color: 'grey.400', '&:hover': { color: 'primary.main' } }}
                        >
                          <ThreeSixty />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {title && (
                      <Typography sx={{ fontSize: 15, color: 'grey.600' }}>{title}</Typography>
                    )}
                    {upc && (
                      <Typography sx={{ fontSize: 12, mt: 0.25 }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}
                        >
                          UPC
                        </Box>
                        <Box component="span" sx={{ color: 'grey.500' }}>
                          {upc}
                        </Box>
                      </Typography>
                    )}
                    {mpn && (
                      <Typography sx={{ fontSize: 12 }}>
                        <Box
                          component="span"
                          sx={{ fontWeight: 700, color: 'grey.600', mr: '4px' }}
                        >
                          MPN
                        </Box>
                        <Box component="span" sx={{ color: 'grey.500' }}>
                          {mpn}
                        </Box>
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
                      {isSnowflakeSku && currentInventoryDetails[0].inspection && (
                        <>
                          <Chip label="Refurbished" color="secondary" size="small" />
                          <InspectionStatusBadge
                            statusId={currentInventoryDetails[0].inspection.statusId}
                            label={currentInventoryDetails[0].inspection.status?.name || 'Unknown'}
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Bin locations (right column) */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    {(() => {
                      const groupedByZone = groupInventoryByZone(currentInventoryDetails);
                      return groupedByZone.map((inv, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '2px',
                          }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'grey.700' }}>
                            {inv.zoneLabel}
                          </Typography>
                          {inv.bins.map((bin, binIndex) => (
                            <Box
                              key={binIndex}
                              sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}
                            >
                              <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'grey.800' }}>
                                {bin.binCode}
                              </Typography>
                              <Typography sx={{ fontSize: 14, color: 'grey.800' }}>
                                qty {bin.quantity}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ));
                    })()}
                  </Box>
                </Box>
              </Box>
            );
          })()}
      </Box>
      {historyItems.length > 0 && (
        <Accordion sx={{ mt: 2, borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>SKU History</AccordionSummary>
          <AccordionDetails sx={{ padding: 1, overflow: 'visible' }}>
            <Box style={{ width: '100%' }}>
              <StripedDataGrid
                rows={historyItems}
                columns={historyColumns}
                getRowId={row => `${row.type}-${row.id}`}
                onRowClick={handleRowClick}
                disableRowSelectionOnClick
                paginationModel={paginationModel}
                onPaginationModelChange={model => {
                  setPaginationModel(model);
                }}
                pageSizeOptions={[PAGE_SIZE, PAGE_SIZE * 2, PAGE_SIZE * 4]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: PAGE_SIZE },
                  },
                }}
                sx={{ border: 'none' }}
                autoHeight
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </PageWrapper>
  );
};

export default InventoryLocationDetails;
